import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { dashboardAPI as apiConfig } from '../../../axiosConfig';
import { sortByProperty, getEncodedURL } from '../../../utils/utils';

const URL = 'payment-options-proxy?endpoint=paymentdashboard';

export const getLOBs = createAsyncThunk('merchants', async () => {
  const url = `${URL}/merchants`;
  const response = await apiConfig.get(url).catch((error) => {
    console.error(JSON.stringify(error));
    return error && error.response;
  });

  return response.data;
});

export const getLOBDetails = createAsyncThunk(
  'merchants/{lobId}',
  async (lobId) => {
    const url = `${URL}/merchants/${lobId}`;
    const response = await apiConfig.get(url).catch((error) => {
      console.error(JSON.stringify(error));
      return error && error.response;
    });
    if (
      response.data.status === 'SUCCESS' &&
      response.data.properties === null
    ) {
      response.data.properties = {};
    }
    return response.data;
  },
);

export const getLOBMethodsByCurrencyAndPlatform = createAsyncThunk(
  'merchants/{id}/methods',
  async ({ id, currency, platform }, { rejectWithValue }) => {
    try {
      const url = getEncodedURL(`${URL}/lobs/${id}/methods`, {
        currencyCode: currency,
        platformCode: platform,
      });
      const response = await apiConfig.get(url);

      if (!response) {
        return rejectWithValue(response);
      }

      // Deep copy the response to prevent error on next fetch
      const responseCopy = JSON.parse(JSON.stringify(response.data));
      responseCopy.data.sort((a, b) => a.order - b.order);
      responseCopy.data.forEach((e) =>
        e.channels.sort((a, b) => a.tabOrder - b.tabOrder),
      );
      return responseCopy;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const addNewMerchant = createAsyncThunk(
  'merchants/{id}/addNew',
  async (merchant) => {
    const url = `${URL}/merchants`;
    const response = await apiConfig.post(url, merchant).catch((error) => {
      console.error(JSON.stringify(error));
      return error && error.response;
    });

    return response.data;
  },
);

export const updateMerchant = createAsyncThunk(
  'merchants/{id}/update',
  async (payload) => {
    const url = `${URL}/merchants/${payload.id}`;
    const response = await apiConfig.put(url, payload).catch((error) => {
      console.error(JSON.stringify(error));
      return error && error.response;
    });

    return response.data;
  },
);

export const getMerchantTabPriority = createAsyncThunk(
  'merchants/{merchantId}/priority',
  async (merchantId, { rejectWithValue }) => {
    const url = `${URL}/priority/${merchantId}`;
    const { data } = await apiConfig.get(url).catch((error) => {
      console.error(JSON.stringify(error));
      return rejectWithValue(error && error.response);
    });

    if (data.status === 'SUCCESS' && data.properties === null) {
      return rejectWithValue(data);
    }

    return data;
  },
);

export const updateMerchantTabPriority = createAsyncThunk(
  'merchants/{merchantId}/priority/update',
  async (payload, { rejectWithValue }) => {
    const url = `${URL}/priority`;
    const response = await apiConfig.put(url, payload).catch((error) => {
      console.error(JSON.stringify(error));
      return rejectWithValue(error && error.response);
    });

    return response.data;
  },
);

const paymentOptionsMerchantsSlice = createSlice({
  name: 'merchants',
  initialState: {
    isFetchingLOBList: false,
    lobList: [],

    selectedMerchant: {},
    merchantMethods: [],
  },
  reducers: {
    resetSelectedMerchant(state) {
      state.selectedMerchant = {};
      state.merchantMethods = [];
    },
    setMerchantTabWithDefault(state, { payload }) {
      state.selectedMerchant.tabs = payload;
    },
  },
  extraReducers: {
    [getLOBs.pending]: (state) => {
      state.isFetchingLOBList = true;
      state.lobList = [];
    },
    [getLOBs.rejected]: (state, action) => {
      state.isFetchingLOBList = false;
      state.lobList = [];
    },
    [getLOBs.fulfilled]: (state, action) => {
      state.isFetchingLOBList = false;

      if (!action.payload || !action.payload.merchants) {
        state.lobList = [];
        return;
      }

      state.lobList = sortByProperty(action.payload.merchants, 'lobName');
    },
    [getLOBDetails.fulfilled]: (state, action) => {
      if (!action.payload || !action.payload.lobId) {
        state.selectedMerchant = {};
        return;
      }

      state.selectedMerchant = action.payload;
    },
    [getMerchantTabPriority.fulfilled]: (state, action) => {
      state.selectedMerchant = {
        ...state.selectedMerchant,
        tabs: action.payload.priorities || [],
      };
    },
    [getLOBMethodsByCurrencyAndPlatform.fulfilled]: (state, action) => {
      if (!action.payload || !action.payload.data) {
        state.merchantMethods = [];
        return;
      }

      state.merchantMethods = action.payload.data;
    },
    [getLOBMethodsByCurrencyAndPlatform.rejected]: (state, action) => {
      state.merchantMethods = [];
    },
  },
});

export const selectIsFetchingLOBList = (state) =>
  state.paymentOptions.merchants.isFetchingLOBList;

export const selectLOBList = (state) => state.paymentOptions.merchants.lobList;

export const selectSelectedMerchant = (state) =>
  state.paymentOptions.merchants.selectedMerchant;

export const selectSelectMerchantMethods = (state) =>
  state.paymentOptions.merchants.merchantMethods;

export const paymentOptionsMerchantsActions =
  paymentOptionsMerchantsSlice.actions;

export default paymentOptionsMerchantsSlice.reducer;
